<template>
  <v-card class="d-flex flex-column" height="100vh">
    <v-sheet class="flex-grow-1 overflow-auto">
      <v-toolbar :color="model?.is_safety ? 'safety' : 'primary'" density="compact" class="px-4">
        <v-icon icon="mdi-engine" class="mr-2"></v-icon>
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          {{ model?.asset_id }}
          <span v-if="model?.description_primary" class="font-weight-regular opacity-90">
        - {{ model.description_primary }}
      </span>
        </v-toolbar-title>
        <v-spacer/>
        <v-btn v-if="hasPosition" prepend-icon="mdi-map-marker-radius" variant="text" @click="showPosition">
          {{ t('assetPosition.showPosition') }}
        </v-btn>
      </v-toolbar>

      <v-tabs
          v-model="currentTab"
          :items="tabs"
          align-tabs="center"
          color="primary"
          height="60"
          slider-color="primary"
      >
        <template v-slot:tab="{ item }">
          <v-tab
              :prepend-icon="item.icon"
              :text="item.text"
              :value="item.value"
              class="text-none"
              :disabled="item.value == 'safety' && !model?.is_safety"
          ></v-tab>
        </template>
        <template v-slot:item="{ item }">
          <v-tabs-window-item :value="item.value" class="pa-4">
            <v-container>
              <v-row>
                <v-col
                    v-for="header in props.headers.filter(h => h.tab == item.value)"
                    :key="header.key"
                    cols="12"
                    md="6"
                >
                  <div
                      v-if="header.key === 'dil'"
                      class="d-flex align-center dil-container"
                  >
                    <v-icon
                        class="mr-7"
                        icon="mdi-toggle-switch"
                        size="24"
                        style="color: rgb(121,121,121);"
                    />
                    <DIL_Switches
                        v-if="model?.is_safety"
                        v-model="dil"
                        :readonly="props.readonly"
                        class="flex-grow-1 mt-2 mb-2"
                    />
                  </div>
                  <v-text-field
                      v-else-if="!header.checkbox"
                      :clearable="!props.readonly"
                      :label="`${header.title}`"
                      :model-value="`${model[header.key]}${getUnitFromKey(header.key)}`"
                      :prepend-icon="header.icon"
                      :readonly="props.readonly"
                      :variant="useGlobalSetting().globalTextVariant"
                  />
                  <v-switch
                      v-else
                      :color="header.key === 'is_safety' ? 'yellow' : 'primary'"
                      :label="header.title"
                      :model-value="model[header.key]"
                      :prepend-icon="header.icon"
                      :readonly="props.readonly"
                      class="flex-no-grow"
                      density="compact"
                      hide-details
                      inset
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-tabs-window-item>
        </template>
      </v-tabs>
    </v-sheet>

    <v-card-actions class="bg-surface-light">
      <v-btn @click="close">
        <v-icon color="black" icon="mdi-close-circle-outline" start></v-icon>
        Schließen
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts" setup>
import {Drive} from "@/types/Drive";
import DIL_Switches from "@/components/misc/DIL_Switches.vue";
import {decimalToBinaryArray, t, useGlobalSetting} from "@/lib/utils";
import {computed, ref, shallowRef} from "vue";
import {DataTableHeader} from "vuetify/lib/components/VDataTable/types";
import {Header} from "@/lib/types";
import {canShowPosition, openAssetPositionMap} from "@/lib/assetPosition";

const model = defineModel<Drive>();
const hasPosition = computed(() => canShowPosition(model.value));
const emit = defineEmits<{ close: [] }>();

const props = defineProps<{
  headers: Header[],
  readonly: boolean,
  onClose?: () => void
}>();

interface Tab {
  icon: string;
  text: string;
  value: string;
}

const currentTab = shallowRef<string>('tab-1')

const tabs: Tab[] = [
  {
    icon: 'mdi mdi-information-slab-box',
    text: 'Allgemeines',
    value: 'general'
  },
  {
    icon: 'mdi-flash',
    text: 'Elektrisches',
    value: 'electrical'
  },
  {
    icon: 'mdi-shield-check',
    text: 'Safety',
    value: 'safety'
  },
  {
    icon: 'mdi-dots-horizontal',
    text: 'Sonstiges',
    value: 'misc'
  },
]

const dil = ref<Array<Boolean>>(decimalToBinaryArray(model.value.safety_code));

const getUnitFromKey = (key: string): string => {
  if(model.value == null) return '';
  if (key === "systemVoltage")
    return model.value.systemVoltageUnit
  if (key === "systemPower")
    return model.value?.systemPowerUnit
  if (key === "systemCurrent")
    return model.value?.systemCurrentUnit
  if (key === "systemNominalSpeed")
    return " " + model.value?.systemNominalSpeedUnit
  return ""
}

function close() {
  props.onClose?.();
  emit('close');
}

function showPosition() {
  if (model.value) openAssetPositionMap(model.value);
}

</script>

<style scoped>
.flex-no-grow {
  flex: 0 0 auto;
  width: auto !important;
  height: auto !important;
}
</style>
